/* ═══════════════════════════════════════════════════════════════════
   BLISS ECHO DECKS — 10 themed emoji categories × 10 cycles (A→J).
   Round order (loops): animals → birds → fishes → insects → flowers
                       → fruits → emojis → objects → celestial → symbols
   Rounds  1-10   = Cycle A
   Rounds 11-20   = Cycle B
   Rounds 21-30   = Cycle C
   Rounds 31-40   = Cycle D
   Rounds 41-50   = Cycle E
   Rounds 51-60   = Cycle F
   Rounds 61-70   = Cycle G
   Rounds 71-80   = Cycle H
   Rounds 81-90   = Cycle I
   Rounds 91-100  = Cycle J
   Rounds 101+    = loops back to Cycle A

   INVARIANT: within each cycle of 12, emojis are UNIQUE (game requires it).
   Across cycles, emojis may repeat for sparse categories (Insects, Flowers,
   Fishes, Symbols). Rich categories (Animals, Objects, Emojis, Celestial)
   attempt no repeats until later cycles.
   ═══════════════════════════════════════════════════════════════════ */

export const CATEGORY_ORDER = [
  'animals', 'birds', 'fishes', 'insects', 'flowers',
  'fruits',  'emojis', 'objects', 'celestial', 'symbols',
];

export const CYCLES = 10;
export const CYCLE_LABELS = ['A','B','C','D','E','F','G','H','I','J'];
export const CYCLE_SIZE = 12;

/* Each category's `cycles` is an array of arrays of 12 emojis.
   Each inner array MUST have 12 unique emojis (game invariant). */
const DECKS = {
  animals: [
    // Mammals + reptiles + mythical — strictly land/mythic creatures.
    ['🐅','🦁','🐘','🦒','🦓','🐒','🐼','🦘','🐺','🦊','🦌','🦏'],
    ['🐻','🐫','🐆','🐎','🐃','🐗','🦥','🦦','🐕','🐈','🐄','🐖'],
    ['🐑','🐐','🦙','🦬','🦣','🦍','🦧','🐇','🐁','🐿️','🦫','🦡'],
    ['🦨','🦔','🐩','🦝','🐀','🐹','🐰','🐴','🐕‍🦺','🐈‍⬛','🐶','🐱'],
    ['🦄','🐲','🐉','🐾','🐭','🐮','🐷','🐨','🐻‍❄️','🐯','🐽','🐊'],
    ['🦎','🐍','🦖','🦕','🐅','🦁','🐘','🦒','🐒','🐼','🐕','🐈'],
    ['🐎','🦓','🦘','🦏','🐫','🐐','🐄','🐖','🐑','🦙','🐇','🦡'],
    ['🐩','🐁','🐿️','🦔','🦨','🦝','🦣','🦥','🦦','🦫','🐹','🐀'],
    ['🐨','🦍','🦧','🐻','🐆','🐺','🦁','🐘','🦒','🐒','🐼','🐊'],
    ['🦎','🐍','🦖','🦕','🐈','🦊','🦌','🐴','🐗','🐮','🐷','🦄'],
  ],
  birds: [
    // Only birds (plus bird-related: feather, egg, nest, wing).
    ['🦅','🦉','🦚','🦜','🦩','🦢','🦆','🐓','🐥','🦃','🕊️','🐧'],
    ['🐦','🪶','🐔','🐣','🪺','🥚','🦤','🪽','🐤','🐦‍⬛','🐦‍🔥','🪿'],
    ['🦅','🦉','🦚','🦜','🦩','🦢','🦆','🐓','🐥','🦃','🕊️','🐧'],
    ['🐦','🐔','🐣','🐤','🪶','🦃','🦤','🐦‍⬛','🪽','🦅','🦉','🕊️'],
    ['🐦','🦚','🦜','🦩','🦢','🦆','🐓','🐥','🦃','🐔','🐣','🐤'],
    ['🦅','🐔','🦉','🐧','🦚','🦩','🐦','🦜','🕊️','🐤','🦆','🐓'],
    ['🐥','🦃','🦤','🐦‍⬛','🪿','🪽','🪶','🪺','🥚','🐣','🦢','🐦‍🔥'],
    ['🦅','🦉','🐧','🦩','🕊️','🦢','🦆','🐤','🐥','🐔','🐣','🐦'],
    ['🦚','🦜','🐓','🦃','🦤','🐦‍⬛','🪶','🪿','🪽','🦅','🦉','🕊️'],
    ['🦩','🐧','🦚','🦜','🦢','🦆','🐓','🐥','🦃','🐦','🪺','🥚'],
  ],
  fishes: [
    // Only aquatic — fish, sea mammals, crustaceans, mollusks, jellyfish, turtle, frog.
    ['🐟','🐠','🐡','🦈','🐳','🐬','🐙','🦑','🦐','🦀','🦞','🦪'],
    ['🦭','🐢','🐸','🐋','🪼','🐚','🪸','🐟','🐠','🐡','🦈','🐳'],
    ['🐬','🐙','🦑','🦐','🦀','🦞','🦪','🦭','🐢','🐸','🐋','🪼'],
    ['🐚','🪸','🐟','🐠','🐡','🦈','🐳','🐬','🐙','🦑','🦐','🦀'],
    ['🦞','🦪','🦭','🐢','🐸','🐋','🪼','🐚','🪸','🐟','🐠','🐡'],
    ['🦈','🐳','🐬','🐙','🦑','🦐','🦀','🦞','🦪','🦭','🐢','🐸'],
    ['🐋','🪼','🐚','🪸','🐟','🐠','🐡','🦈','🐳','🐬','🐙','🦑'],
    ['🦐','🦀','🦞','🦪','🦭','🐢','🐸','🐋','🪼','🐚','🪸','🐟'],
    ['🐠','🐡','🦈','🐳','🐬','🐙','🦑','🦐','🦀','🦞','🦪','🦭'],
    ['🐢','🐸','🐋','🪼','🐚','🪸','🐟','🐠','🐡','🦈','🐳','🐬'],
  ],
  insects: [
    // Bugs + arachnids + worms + snails — creepy-crawlies only.
    ['🐝','🐞','🦋','🐜','🕷️','🪲','🪳','🦗','🐛','🦟','🪰','🐌'],
    ['🦂','🕸️','🦠','🪱','🦋','🐝','🐜','🪲','🪳','🦗','🕷️','🐛'],
    ['🦟','🪰','🐌','🐞','🦂','🦠','🪱','🐝','🕸️','🦋','🐜','🪲'],
    ['🪳','🦗','🕷️','🐛','🦟','🪰','🐌','🦂','🕸️','🦠','🪱','🐞'],
    ['🦋','🐝','🐜','🪲','🪳','🦗','🕷️','🕸️','🦟','🪰','🐛','🐌'],
    ['🐞','🦂','🦠','🪱','🦋','🐝','🐜','🪲','🪳','🦗','🕷️','🕸️'],
    ['🦟','🪰','🐛','🐌','🐞','🦂','🦠','🪱','🐝','🦋','🐜','🪲'],
    ['🪳','🦗','🕷️','🕸️','🦟','🪰','🐛','🐌','🐞','🦂','🦠','🪱'],
    ['🐝','🦋','🐜','🪲','🪳','🦗','🕷️','🕸️','🦟','🪰','🐛','🐌'],
    ['🐞','🦂','🦠','🪱','🦋','🐝','🐜','🪲','🪳','🦗','🕷️','🐛'],
  ],
  flowers: [
    ['🌸','🌼','🌻','🌺','🌹','🥀','🌷','💐','🪷','🪻','🌱','🌿'],
    ['🍀','🍃','🌾','🌵','🎋','🪴','🍁','🍂','🌳','🌲','🌴','🎍'],
    ['🪷','🌸','🌺','🌷','🌹','🌻','🌼','🥀','💐','🪻','🌾','🌱'],
    ['🌿','🍀','🎋','🪴','🌸','🌼','🌻','🌺','🌹','🥀','🌷','💐'],
    ['🪷','🪻','🌱','🌿','🍀','🍃','🌾','🌵','🎋','🪴','🍁','🍂'],
    ['🌳','🌲','🌴','🎍','🌸','🌼','🌻','🌺','🌹','🥀','🌷','💐'],
    ['🪷','🪻','🌱','🌿','🍀','🍃','🌾','🌵','🎋','🪴','🍁','🍂'],
    ['🌳','🌲','🌴','🎍','🌸','🌼','🌻','🌺','🌹','🥀','🌷','💐'],
    ['🪷','🪻','🌱','🌿','🍀','🍃','🌾','🌵','🎋','🪴','🍁','🍂'],
    ['🌳','🌲','🌴','🎍','🌸','🌼','🌻','🌺','🌹','🥀','🌷','💐'],
  ],
  fruits: [
    ['🍎','🍌','🍒','🍇','🍋','🥭','🍑','🍐','🍍','🥥','🥝','🍈'],
    ['🍉','🍓','🫐','🥑','🫒','🍅','🍊','🍏','🌰','🥜','🫘','🥕'],
    ['🌽','🥔','🍠','🌶️','🫑','🥒','🥬','🥦','🧄','🧅','🍆','🍄'],
    ['🥯','🍞','🥐','🥨','🍎','🍌','🍒','🍇','🍋','🥭','🍑','🍐'],
    ['🍍','🥥','🥝','🍈','🍉','🍓','🫐','🥑','🫒','🍅','🍊','🍏'],
    ['🌰','🥜','🫘','🥕','🌽','🥔','🍠','🌶️','🫑','🥒','🥬','🥦'],
    ['🧄','🧅','🍆','🍄','🥯','🍞','🥐','🥨','🍎','🍌','🍒','🍇'],
    ['🍋','🥭','🍑','🍐','🍍','🥥','🥝','🍈','🍉','🍓','🫐','🥑'],
    ['🫒','🍅','🍊','🍏','🌰','🥜','🫘','🥕','🌽','🥔','🍠','🌶️'],
    ['🫑','🥒','🥬','🥦','🧄','🧅','🍆','🍄','🥯','🍞','🥐','🥨'],
  ],
  emojis: [
    ['😀','😂','😍','😎','🤩','😇','😋','😜','🤔','😴','🥳','😭'],
    ['😱','😡','🤯','🥶','🥵','🤠','🤗','🤫','🙂','😉','😊','🥰'],
    ['😘','😗','😌','😔','😪','🤤','🤒','🤕','🤢','🤧','😷','🥴'],
    ['😵','🤥','🤓','🧐','🤡','👻','💀','👽','🤖','😺','😸','😹'],
    ['😻','😼','😽','🙀','😿','😾','🥺','🥹','😤','😠','🤬','🤨'],
    ['😐','😑','😶','🫥','🫤','🙃','🫡','🫠','🤭','🤗','🙄','😏'],
    ['😒','🥲','😢','😥','😓','🫨','🥱','😯','😦','😧','😮','😲'],
    ['🤐','🤫','🫢','🫣','😬','😰','😨','😳','🤪','😝','😛','😙'],
    ['🤑','🤠','🥸','😎','🤓','🧐','🥶','🥵','🤒','🤕','🤢','🤮'],
    ['🥱','😪','😴','🤤','😷','🤧','😇','🥳','🤩','🤗','🤠','🫡'],
  ],
  objects: [
    ['💎','👑','🗝️','⚔️','🛡️','🔔','📕','🚀','⚓','🧭','⏳','🏮'],
    ['🕯️','🌍','🏆','🎖️','💍','📷','🎸','♟️','🎩','🎒','👜','🕶️'],
    ['⌚','📱','💻','🖥️','🖱️','⌨️','🖨️','📞','📺','🎧','🎙️','🎥'],
    ['📽️','🎬','🎮','🕹️','🎲','🎯','🎳','🎪','🎭','🎨','🎼','🎹'],
    ['🎺','🎷','🥁','🎻','🪕','🪘','🪗','🎤','🎞️','📯','🔮','🔦'],
    ['🕰️','⏰','⌛','🔋','🔌','💡','🪔','📔','📓','📗','📘','📙'],
    ['📚','📖','🔖','🧷','🔗','📎','🖇️','📐','📏','🗂️','📁','📂'],
    ['🗒️','🗓️','📅','📆','📇','🗃️','🗳️','🗄️','📊','📈','📉','📋'],
    ['📌','📍','✏️','🖊️','🖋️','🖌️','🖍️','📝','✒️','💼','👝','🎁'],
    ['🎀','🎊','🎉','🪅','🪩','🧧','🏷️','🔍','🔎','🔏','🔐','🔒'],
  ],
  celestial: [
    ['☀️','🌙','⭐','✨','☄️','🌈','☁️','🔥','❄️','⚡','🌧️','🌊'],
    ['🌍','🪐','🌌','🌋','🌪️','🌠','🌑','🌇','🌞','🌛','🌜','🌚'],
    ['🌝','🌒','🌓','🌔','🌕','🌖','🌗','🌘','🌤️','⛅','🌥️','🌦️'],
    ['🌩️','🌨️','🌬️','🌫️','🌎','🌏','🌐','🗺️','🌅','🌄','🌆','🌃'],
    ['🎑','🌉','🏙️','🌁','🎇','🎆','🌌','🌟','💫','⛈️','☃️','⛄'],
    ['🌡️','🌂','☔','🪐','🌏','🌎','🌍','🌐','☀️','🌙','⭐','✨'],
    ['☄️','🌈','☁️','🔥','❄️','⚡','🌧️','🌊','🌋','🌪️','🌠','🌑'],
    ['🌇','🌞','🌛','🌜','🌚','🌝','🌒','🌓','🌔','🌕','🌖','🌗'],
    ['🌘','🌤️','⛅','🌥️','🌦️','🌩️','🌨️','🌬️','🌫️','🌅','🌄','🌆'],
    ['🌃','🎑','🌉','🏙️','🌁','🎇','🎆','🌟','💫','⛈️','☃️','⛄'],
  ],
  symbols: [
    ['♠️','♣️','♥️','♦️','☯️','☮️','⚜️','🔱','⚛️','☸️','✝️','☪️'],
    ['🕉️','✡️','☦️','☬','🕎','🛐','⛎','♾️','♈','♉','♊','♋'],
    ['♌','♍','♎','♏','♐','♑','♒','♓','⚕️','⚖️','⚙️','⚗️'],
    ['⚔️','⚡','🔰','💠','🔷','🔶','🔸','🔹','🔺','🔻','🔴','🟠'],
    ['🟡','🟢','🔵','🟣','⚫','⚪','🟤','🟥','🟧','🟨','🟩','🟦'],
    ['🟪','🟫','⬛','⬜','◼️','◻️','◾','◽','▪️','▫️','🔲','🔳'],
    ['💯','💢','💥','💫','💦','💨','🕳️','💣','💬','💭','🗨️','🗯️'],
    ['♠️','♣️','♥️','♦️','☯️','☮️','⚜️','🔱','⚛️','☸️','✝️','☪️'],
    ['🕉️','✡️','☦️','☬','🕎','🛐','⛎','♾️','♈','♉','♊','♋'],
    ['♌','♍','♎','♏','♐','♑','♒','♓','⚕️','⚖️','⚙️','⚗️'],
  ],
};

const ACCENTS = {
  animals: '#E9A44C', birds: '#5DD4FF', fishes: '#48D1CC',
  insects: '#8BE07A', flowers: '#FF9DBF', fruits: '#5BE08A',
  emojis: '#FFC857', objects: '#B57EDC', celestial: '#FFE28A',
  symbols: '#FF8EC7',
};

const LABELS = {
  animals: 'Animals', birds: 'Birds', fishes: 'Fishes',
  insects: 'Insects', flowers: 'Flowers', fruits: 'Fruits',
  emojis: 'Emojis',  objects: 'Objects', celestial: 'Celestial',
  symbols: 'Symbols',
};

/* Given a total round count (0-indexed), return the deck to play. */
export function deckForRound(roundIdx) {
  const catIdx   = roundIdx % CATEGORY_ORDER.length;                     // 0..9
  const cycleIdx = Math.floor(roundIdx / CATEGORY_ORDER.length) % CYCLES; // 0..9
  const catKey = CATEGORY_ORDER[catIdx];
  const slice = DECKS[catKey][cycleIdx];
  const entries = slice.map((em, i) => ({ name: `${catKey}_${cycleIdx}_${i}`, emoji: em }));
  return {
    id: catKey,
    label: LABELS[catKey],
    accent: ACCENTS[catKey],
    cycleIdx,
    cycleLabel: CYCLE_LABELS[cycleIdx],
    entries,
  };
}

/* Legacy export retained for anything else that imports it. */
export const BLISS_DECKS = Object.fromEntries(
  CATEGORY_ORDER.map((k) => [k, {
    label: LABELS[k], accent: ACCENTS[k],
    entries: DECKS[k][0].map((em, i) => ({ name: `${k}_0_${i}`, emoji: em })),
  }])
);

/* Exported so tests / verifiers can iterate without duplicating structure. */
export const _DECKS_INTERNAL = DECKS;
